<template>
  <StepsIndicator />

  <BaseSection id="estimation" :nextStep="true" @onNextStepClick="projectBase.onNextStepClick">
    <h2 class="text-xl">{{ wt('estimation', 'title') }}</h2>
    <HelpText slug="project_service_estimation" :role="roleUpper" />
    <ServiceEstimationForm v-model="formData" :role="roleUpper" :serviceCalculator="serviceCalculator" />
  </BaseSection>

  <BaseSection
    id="effort"
    @onNextModuleClick="projectBase.onNextModuleClick(partKey, formData, 'summary-project-view', undefined, role)"
    nextModule="summary">
    <h2 class="text-xl">{{ wt('effort', 'title') }}</h2>
    <HelpText slug="project_service_effort" :role="roleUpper" />
    <ServiceEffortForm v-model="formData" :role="roleUpper" :serviceCalculator="serviceCalculator" />
  </BaseSection>
</template>

<script setup lang="ts">
import { onMounted, computed, ref, type Ref, watch } from 'vue'
import BaseSection from '@/components/partials/layout/BaseSection.vue'
import StepsIndicator from '@/components/partials/navigation/StepsIndicator.vue'
import HelpText from '@/components/partials/display/HelpText.vue'
import { onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router'
import projectPageBase from '@/base/projectPageBase'
import type { NestedObject } from '@/types'
import { useWidgetText } from '@/helpers/useWidgetText'
import { deepClone } from '@/helpers/objects_arrays'

// Import shared form components
import ServiceEstimationForm from '@/components/project/shared/ServiceEstimationForm.vue'
import ServiceEffortForm from '@/components/project/shared/ServiceEffortForm.vue'

// Import calculators
import {
  useProjectArchitectCalculator,
  useProjectLandscapeArchitectCalculator,
  useProjectCivilEngineerCalculator,
  useProjectCivilEngineerExcavationCalculator,
  useProjectElectricalCalculator,
  useProjectEngineerCalculator,
  useProjectHeatingCoolingCalculator,
  useProjectVentilationACCalculator,
  useProjectHVACCalculator,
  useProjectSanitaryCalculator,
  useProjectBuildingAutomationCalculator,
  useProjectSpecialistCoordinationCalculator,
} from '@/stores/projectCenter/projectServiceCalculatorFactory'

export type CompanyType =
  | 'architect'
  | 'landscape_architect'
  | 'civil_engineer'
  | 'civil_engineer_excavation'
  | 'electrical'
  | 'engineer'
  | 'heating_cooling'
  | 'ventilation_ac'
  | 'hvac'
  | 'sanitary'
  | 'building_automation'
  | 'specialist_coordination'

// Props
const props = defineProps<{
  companyType: CompanyType
  role?: string
}>()

// Computed values based on company type
const partKey = computed(() => `${props.companyType}_service`)
const roleByCompanyType: Record<CompanyType, string> = {
  architect: 'ARCHITECT',
  landscape_architect: 'LANDSCAPE_ARCHITECT',
  civil_engineer: 'CIVIL_ENGINEER',
  civil_engineer_excavation: 'CIVIL_ENGINEER_EXCAVATION',
  electrical: 'ELECTRICAL',
  engineer: 'ENGINEER',
  heating_cooling: 'HEATING_COOLING',
  ventilation_ac: 'VENTILATION_AC',
  hvac: 'HVAC',
  sanitary: 'SANITARY',
  building_automation: 'BUILDING_AUTOMATION',
  specialist_coordination: 'SPECIALIST_COORDINATION',
}

const serviceSetterByCompanyType: Record<CompanyType, string> = {
  architect: 'setProjectArchitectServiceData',
  landscape_architect: 'setProjectLandscapeArchitectServiceData',
  civil_engineer: 'setProjectCivilEngineerServiceData',
  civil_engineer_excavation: 'setProjectCivilEngineerExcavationServiceData',
  electrical: 'setProjectElectricalServiceData',
  engineer: 'setProjectEngineerServiceData',
  heating_cooling: 'setProjectHeatingCoolingServiceData',
  ventilation_ac: 'setProjectVentilationACServiceData',
  hvac: 'setProjectHVACServiceData',
  sanitary: 'setProjectSanitaryServiceData',
  building_automation: 'setProjectBuildingAutomationServiceData',
  specialist_coordination: 'setProjectSpecialistCoordinationServiceData',
}

const roleUpper = computed(() => roleByCompanyType[props.companyType])

const { wt } = useWidgetText('widget-texts-project-service', roleUpper)

// Calculator mapping
const calculatorMap = {
  architect: useProjectArchitectCalculator,
  landscape_architect: useProjectLandscapeArchitectCalculator,
  civil_engineer: useProjectCivilEngineerCalculator,
  civil_engineer_excavation: useProjectCivilEngineerExcavationCalculator,
  electrical: useProjectElectricalCalculator,
  engineer: useProjectEngineerCalculator,
  heating_cooling: useProjectHeatingCoolingCalculator,
  ventilation_ac: useProjectVentilationACCalculator,
  hvac: useProjectHVACCalculator,
  sanitary: useProjectSanitaryCalculator,
  building_automation: useProjectBuildingAutomationCalculator,
  specialist_coordination: useProjectSpecialistCoordinationCalculator,
}

// Get the appropriate calculator
const serviceCalculator = computed(() => calculatorMap[props.companyType]())

const formData: Ref<any> = ref()
const projectBase = projectPageBase.useProjectPageBase()

function initForm(): void {
  const project = projectBase.project.value
  const projectPart = project[partKey.value]
  formData.value = { ...projectPart.data } as NestedObject
  projectBase.validator.initForm(formData.value, projectBase.validationSchema)

  if (formData.value && !formData.value.phases) {
    /* @ts-ignore */ // todo fix this ignore
    formData.value.phases = computed(() => serviceCalculator.value.getDefaultPhases)
  }
}

async function onSaveProject(): Promise<void> {
  if (!formData.value) {
    return
  }
  const success = await projectBase.saveProjectPart(partKey.value, formData.value)
  if (success) {
    initForm()
  }
}

function syncCurrentFormToProject(): void {
  if (!formData.value) return
  const projectPart = projectBase.project.value?.[partKey.value]
  if (!projectPart) return
  projectPart.data = deepClone(formData.value)
}

onMounted(() => {
  initForm()
})

watch(
  () => props.companyType,
  () => {
    initForm()
  }
)

onBeforeRouteUpdate(async (to, from, next) => {
  await projectBase.checkFormIsDirty(next, { type: partKey.value, data: formData.value })
})
onBeforeRouteLeave(async (to, from, next) => {
  await projectBase.checkFormIsDirty(next, { type: partKey.value, data: formData.value })
})

watch(
  formData,
  () => {
    projectBase.validator.onFormValuesChange(formData.value as NestedObject)

    // Call the appropriate calculator method based on company type
    const methodName = serviceSetterByCompanyType[props.companyType]
    const calculator = projectBase.calculator as any
    if (calculator[methodName]) {
      calculator[methodName](formData.value)
    }
  },
  { deep: true, immediate: false }
)

defineExpose({
  onSaveProject,
  syncCurrentFormToProject,
})
</script>
